/**
 * LinkTree 3D Interactive Card
 * Immersive holographic card with parallax/tilt effects
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiMail,
  FiGithub,
  FiLinkedin,
  FiTwitter,
  FiExternalLink,
  FiRotateCw,
  FiGlobe,
  FiDownload
} from 'react-icons/fi';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import SEO from '../components/SEO';

// Custom hook for cycling through values
const useCycleValue = (values, intervalMs = 3000) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex(prev => (prev + 1) % values.length);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [values.length, intervalMs]);

  return values[index];
};

const LinkTree3D = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFlipped, setIsFlipped] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [glarePosition, setGlarePosition] = useState({ x: 50, y: 50 });
  const [cyclingEmailIndex, setCyclingEmailIndex] = useState(0);
  const cardRef = useRef(null);
  const { showToast: _showToast } = useToast();

  // Calculate dynamic values
  const currentYear = new Date().getFullYear();
  const yearsInTech = currentYear - 1987;
  const yearsTeaching = currentYear - 2011;

  // Cycling stats
  const middleStat = useCycleValue([
    { number: `${yearsTeaching}+`, text: 'Years Teaching' },
    { number: '4', text: 'PhD Students' },
    { number: '4', text: 'Startups Sold' },
  ], 3000);

  const rightStat = useCycleValue([
    { number: 'RPI', text: 'Professor' },
    { number: 'TSI', text: 'Owner' },
    { number: 'Appatura', text: 'CEO' },
  ], 3500);

  // Group links by type for front display
  const groupedLinks = {
    social: links.filter(l => ['linkedin', 'github', 'twitter'].includes(l.icon?.toLowerCase())),
    email: links.filter(l => l.url?.startsWith('mailto:')),
    portfolio: links.filter(l => !['linkedin', 'github', 'twitter'].includes(l.icon?.toLowerCase()) && !l.url?.startsWith('mailto:'))
  };

  // Cycle through emails
  useEffect(() => {
    if (groupedLinks.email.length > 1) {
      const timer = setInterval(() => {
        setCyclingEmailIndex(prev => (prev + 1) % groupedLinks.email.length);
      }, 4000);
      return () => clearInterval(timer);
    }
  }, [groupedLinks.email.length]);

  // All skills/tags for orbiting
  const allSkills = [
    'Thought Leadership', 'Board Member', 'Business Strategy', 'Blockchain',
    'Generative AI', 'Agentic AI', 'AI in Programming', 'AI in Enterprise',
    'Ethics', 'CEO', 'CIO', 'CTO', 'Innovator', 'Inventor', 'Builder',
    'Carpenter', 'Pilot', 'SCUBA', 'Founder'
  ];

  // Create orbital rings with skills distributed
  const innerRing = allSkills.slice(0, 7).map((name, i) => ({
    name,
    angle: (360 / 7) * i,
    ring: 'inner'
  }));

  const outerRing = allSkills.slice(7).map((name, i) => ({
    name,
    angle: (360 / 12) * i + 15, // offset by 15 degrees
    ring: 'outer'
  }));

  const skills = [...innerRing, ...outerRing];

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const response = await api.get('/linktree');
        setLinks(response.data.links || []);
      } catch (error) {
        console.error('Failed to fetch links:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLinks();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current || isFlipped) return;

    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;

    const rotateX = (mouseY / (rect.height / 2)) * -15;
    const rotateY = (mouseX / (rect.width / 2)) * 15;

    setTilt({ x: rotateX, y: rotateY });

    const glareX = ((e.clientX - rect.left) / rect.width) * 100;
    const glareY = ((e.clientY - rect.top) / rect.height) * 100;
    setGlarePosition({ x: glareX, y: glareY });
  }, [isFlipped]);

  const handleMouseLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 });
    setGlarePosition({ x: 50, y: 50 });
  }, []);

  const handleLinkClick = async (link) => {
    try {
      await api.post(`/linktree/${link._id}/click`);
    } catch (error) {
      console.error('Failed to track click:', error);
    }
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  const getIcon = (iconName) => {
    const icons = {
      'linkedin': FiLinkedin,
      'github': FiGithub,
      'twitter': FiTwitter,
      'mail': FiMail,
      'globe': FiGlobe,
    };
    return icons[iconName?.toLowerCase()] || FiExternalLink;
  };

  const flipCard = () => {
    setIsFlipped(!isFlipped);
    setTilt({ x: 0, y: 0 });
  };

  const handleDownloadVCard = async (e) => {
    e.stopPropagation();
    try {
      const response = await api.get('/linktree/vcard', {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/vcard' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Richard_Plotka.vcf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download vCard:', error);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <ParticleField />
        <LoadingCard>
          <PulseRing />
          <LoadingText>Loading...</LoadingText>
        </LoadingCard>
      </PageContainer>
    );
  }

  return (
    <>
      <SEO
        title="Richard Plotka - Connect"
        description="CxO, Professor, Consultant - Connect with Richard Plotka"
        keywords={['Richard Plotka', 'RPI', 'WayFong', 'Professor', 'Consultant']}
      />
      <PageContainer>
        <ParticleField>
          {[...Array(50)].map((_, i) => (
            <Particle key={i} style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }} />
          ))}
        </ParticleField>

        {/* Orbiting Skills - Multiple Rings */}
        <SkillOrbit $isFlipped={isFlipped}>
          {skills.map((skill, index) => (
            <OrbitingSkill
              key={skill.name}
              as={motion.div}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 + index * 0.05 }}
              $ring={skill.ring}
              style={{
                '--angle': `${skill.angle}deg`,
                '--delay': `${index * -1.5}s`
              }}
            >
              {skill.name}
            </OrbitingSkill>
          ))}
        </SkillOrbit>

        <CardContainer
          ref={cardRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={flipCard}
          style={{
            transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`
          }}
        >
          <Card3D $isFlipped={isFlipped}>
            {/* Front Side */}
            <CardFront>
              <HolographicOverlay
                style={{
                  background: `radial-gradient(circle at ${glarePosition.x}% ${glarePosition.y}%, rgba(255,255,255,0.3) 0%, transparent 60%)`
                }}
              />
              <RainbowBorder />

              <CardContent>
                <AvatarContainer>
                  <AvatarRing />
                  <Avatar src="/RMP-Headshot25.jpg" alt="Richard Plotka" />
                  <AvatarGlow />
                </AvatarContainer>

                <ProfileInfo>
                  <Name
                    as={motion.h1}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    Richard Plotka
                  </Name>

                  <TitleContainer>
                    <Title>CxO</Title>
                    <TitleDivider>|</TitleDivider>
                    <Title>Professor</Title>
                    <TitleDivider>|</TitleDivider>
                    <Title>Consultant</Title>
                  </TitleContainer>

                  <Tagline>
                    Transforming Business and Education through effective use of Technology
                  </Tagline>

                  <StatsRow>
                    <StatItem>
                      <StatNumber>{yearsInTech}+</StatNumber>
                      <StatText>Years</StatText>
                    </StatItem>
                    <StatDivider />
                    <StatItem>
                      <AnimatePresence mode="wait">
                        <StatNumber
                          as={motion.div}
                          key={middleStat.number}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3 }}
                        >
                          {middleStat.number}
                        </StatNumber>
                      </AnimatePresence>
                      <AnimatePresence mode="wait">
                        <StatText
                          as={motion.div}
                          key={middleStat.text}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {middleStat.text}
                        </StatText>
                      </AnimatePresence>
                    </StatItem>
                    <StatDivider />
                    <StatItem>
                      <AnimatePresence mode="wait">
                        <StatNumber
                          as={motion.div}
                          key={rightStat.number}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3 }}
                        >
                          {rightStat.number}
                        </StatNumber>
                      </AnimatePresence>
                      <AnimatePresence mode="wait">
                        <StatText
                          as={motion.div}
                          key={rightStat.text}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {rightStat.text}
                        </StatText>
                      </AnimatePresence>
                    </StatItem>
                  </StatsRow>
                </ProfileInfo>

                {/* Quick Links Row */}
                <QuickLinksRow>
                  {/* Social Links */}
                  {groupedLinks.social.map((link) => {
                    const IconComponent = getIcon(link.icon);
                    return (
                      <QuickLinkButton
                        key={link._id}
                        as={motion.button}
                        whileHover={{ scale: 1.15, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => { e.stopPropagation(); handleLinkClick(link); }}
                        title={link.title}
                      >
                        <IconComponent size={18} />
                      </QuickLinkButton>
                    );
                  })}

                  {/* Portfolio Link */}
                  {groupedLinks.portfolio.map((link) => {
                    const isImageIcon = link.icon && (link.icon.startsWith('http') || link.icon.startsWith('/'));
                    return (
                      <QuickLinkButton
                        key={link._id}
                        as={motion.button}
                        whileHover={{ scale: 1.15, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => { e.stopPropagation(); handleLinkClick(link); }}
                        title={link.title}
                      >
                        {isImageIcon ? (
                          <QuickLinkImg src={link.icon} alt={link.title} />
                        ) : (
                          <FiGlobe size={18} />
                        )}
                      </QuickLinkButton>
                    );
                  })}

                  {/* Cycling Email Link */}
                  {groupedLinks.email.length > 0 && (
                    <QuickLinkButton
                      as={motion.button}
                      whileHover={{ scale: 1.15, y: -3 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLinkClick(groupedLinks.email[cyclingEmailIndex]);
                      }}
                      title={groupedLinks.email[cyclingEmailIndex]?.title}
                      $cycling={groupedLinks.email.length > 1}
                    >
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={cyclingEmailIndex}
                          initial={{ opacity: 0, rotateY: 90 }}
                          animate={{ opacity: 1, rotateY: 0 }}
                          exit={{ opacity: 0, rotateY: -90 }}
                          transition={{ duration: 0.3 }}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          {groupedLinks.email[cyclingEmailIndex]?.icon?.startsWith('/') ? (
                            <QuickLinkImg src={groupedLinks.email[cyclingEmailIndex].icon} alt="Email" />
                          ) : (
                            <FiMail size={18} />
                          )}
                        </motion.div>
                      </AnimatePresence>
                    </QuickLinkButton>
                  )}

                  {/* Save Contact Button */}
                  <SaveContactButton
                    as={motion.button}
                    whileHover={{ scale: 1.1, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleDownloadVCard}
                    title="Save Contact"
                  >
                    <FiDownload size={16} />
                    <span>Save</span>
                  </SaveContactButton>
                </QuickLinksRow>

                <FlipIcon
                  as={motion.div}
                  whileHover={{ scale: 1.2, rotate: 180 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FiRotateCw size={20} />
                </FlipIcon>
              </CardContent>
            </CardFront>

            {/* Back Side */}
            <CardBack>
              <HolographicOverlay
                style={{
                  background: `radial-gradient(circle at ${100 - glarePosition.x}% ${glarePosition.y}%, rgba(255,255,255,0.3) 0%, transparent 60%)`
                }}
              />
              <RainbowBorder />

              <BackContent>
                <BackHeader>
                  <BackTitle>Connect With Me</BackTitle>
                  <QRMini src="/LinkQR.png" alt="QR" />
                </BackHeader>

                <LinksList>
                  <AnimatePresence>
                    {links.map((link, index) => {
                      const IconComponent = getIcon(link.icon);
                      const isImageIcon = link.icon && (link.icon.startsWith('http') || link.icon.startsWith('/'));

                      return (
                        <LinkItem
                          key={link._id}
                          as={motion.button}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ scale: 1.02, x: 5 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleLinkClick(link)}
                        >
                          <LinkIconWrapper>
                            {isImageIcon ? (
                              <LinkIconImg src={link.icon} alt={link.title} />
                            ) : (
                              <IconComponent size={18} />
                            )}
                          </LinkIconWrapper>
                          <LinkText>
                            <LinkTitle>{link.title}</LinkTitle>
                            {link.subtitle && <LinkSub>{link.subtitle}</LinkSub>}
                          </LinkText>
                          <FiExternalLink size={14} />
                        </LinkItem>
                      );
                    })}
                  </AnimatePresence>
                </LinksList>

                <FlipIcon
                  as={motion.div}
                  whileHover={{ scale: 1.2, rotate: 180 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={flipCard}
                >
                  <FiRotateCw size={20} />
                </FlipIcon>
              </BackContent>
            </CardBack>
          </Card3D>
        </CardContainer>

        <Footer>
          <FooterText>&copy; {new Date().getFullYear()} Richard Plotka</FooterText>
        </Footer>
      </PageContainer>
    </>
  );
};

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const orbitInner = keyframes`
  from { transform: rotate(0deg) translateX(320px) rotate(0deg); }
  to { transform: rotate(360deg) translateX(320px) rotate(-360deg); }
`;

const orbitOuter = keyframes`
  from { transform: rotate(0deg) translateX(420px) rotate(0deg); }
  to { transform: rotate(-360deg) translateX(420px) rotate(360deg); }
`;

const particleFloat = keyframes`
  0%, 100% {
    transform: translateY(0) translateX(0);
    opacity: 0.3;
  }
  50% {
    transform: translateY(-30px) translateX(10px);
    opacity: 0.8;
  }
`;

const rainbowShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.5); opacity: 0; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.5); }
  50% { box-shadow: 0 0 40px rgba(236, 72, 153, 0.5); }
`;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: radial-gradient(ellipse at center, #1a1a2e 0%, #0a0a0f 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow: hidden;
  position: relative;
`;

const ParticleField = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
`;

const Particle = styled.div`
  position: absolute;
  width: 4px;
  height: 4px;
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  border-radius: 50%;
  animation: ${particleFloat} 5s ease-in-out infinite;
`;

const SkillOrbit = styled.div`
  position: absolute;
  width: 900px;
  height: 900px;
  pointer-events: none;
  opacity: ${props => props.$isFlipped ? 0.3 : 1};
  transition: opacity 0.5s ease;

  @media (max-width: 1200px) {
    width: 700px;
    height: 700px;
  }

  @media (max-width: 768px) {
    width: 500px;
    height: 500px;
  }
`;

const OrbitingSkill = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform-origin: center;
  animation: ${props => props.$ring === 'inner' ? orbitInner : orbitOuter} ${props => props.$ring === 'inner' ? '35s' : '50s'} linear infinite;
  animation-delay: var(--delay);

  padding: 0.4rem 0.8rem;
  background: ${props => props.$ring === 'inner'
    ? 'rgba(139, 92, 246, 0.25)'
    : 'rgba(236, 72, 153, 0.2)'};
  backdrop-filter: blur(10px);
  border: 1px solid ${props => props.$ring === 'inner'
    ? 'rgba(139, 92, 246, 0.4)'
    : 'rgba(236, 72, 153, 0.3)'};
  border-radius: 20px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;

  @media (max-width: 1200px) {
    font-size: 0.65rem;
    padding: 0.35rem 0.7rem;
  }

  @media (max-width: 768px) {
    font-size: 0.55rem;
    padding: 0.3rem 0.6rem;
  }
`;

const CardContainer = styled.div`
  width: 380px;
  height: 520px;
  perspective: 1000px;
  cursor: pointer;
  transition: transform 0.1s ease-out;
  animation: ${float} 6s ease-in-out infinite;
  z-index: 10;

  @media (max-width: 768px) {
    width: 320px;
    height: 460px;
  }
`;

const Card3D = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  transition: transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  transform: ${props => props.$isFlipped ? 'rotateY(180deg)' : 'rotateY(0)'};
`;

const CardSide = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 24px;
  overflow: hidden;
  background: rgba(20, 20, 35, 0.9);
  backdrop-filter: blur(20px);
`;

const CardFront = styled(CardSide)``;

const CardBack = styled(CardSide)`
  transform: rotateY(180deg);
`;

const HolographicOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
  mix-blend-mode: overlay;
`;

const RainbowBorder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 24px;
  padding: 2px;
  background: linear-gradient(
    135deg,
    #ff0080, #ff8c00, #40e0d0, #8b5cf6, #ff0080
  );
  background-size: 400% 400%;
  animation: ${rainbowShift} 8s ease infinite;
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
`;

const CardContent = styled.div`
  position: relative;
  height: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 5;
`;

const AvatarContainer = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const AvatarRing = styled.div`
  position: absolute;
  top: -8px;
  left: -8px;
  right: -8px;
  bottom: -8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6, #ec4899, #ef4444, #8b5cf6);
  background-size: 300% 300%;
  animation: ${rainbowShift} 4s ease infinite, ${glow} 2s ease-in-out infinite;
`;

const Avatar = styled.img`
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #1a1a2e;
  z-index: 1;

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const AvatarGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, transparent 70%);
  animation: ${pulse} 3s ease-in-out infinite;
  z-index: 0;
`;

const ProfileInfo = styled.div`
  text-align: center;
`;

const Name = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
  text-shadow: 0 0 30px rgba(139, 92, 246, 0.5);

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const Title = styled.span`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
`;

const TitleDivider = styled.span`
  color: #8b5cf6;
`;

const Tagline = styled.p`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  margin-bottom: 1.5rem;
  max-width: 300px;
  line-height: 1.4;
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const StatItem = styled.div`
  text-align: center;
  min-width: 70px;
`;

const StatNumber = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StatText = styled.div`
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 30px;
  background: rgba(255, 255, 255, 0.1);
`;

const QuickLinksRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
`;

const QuickLinkButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  ${props => props.$cycling && `
    &::after {
      content: '';
      position: absolute;
      bottom: 2px;
      left: 50%;
      transform: translateX(-50%);
      width: 16px;
      height: 2px;
      background: linear-gradient(90deg, #8b5cf6, #ec4899);
      border-radius: 1px;
    }
  `}

  &:hover {
    background: rgba(139, 92, 246, 0.3);
    border-color: rgba(139, 92, 246, 0.5);
    box-shadow: 0 0 15px rgba(139, 92, 246, 0.4);
  }
`;

const QuickLinkImg = styled.img`
  width: 22px;
  height: 22px;
  border-radius: 4px;
  object-fit: contain;
`;

const SaveContactButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3));
  border: 1px solid rgba(139, 92, 246, 0.4);
  color: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.5), rgba(236, 72, 153, 0.5));
    border-color: rgba(139, 92, 246, 0.7);
    box-shadow: 0 0 20px rgba(139, 92, 246, 0.4);
  }
`;

const FlipIcon = styled.div`
  position: absolute;
  bottom: 0.75rem;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const BackContent = styled.div`
  position: relative;
  height: 100%;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  z-index: 5;
`;

const BackHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const BackTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
`;

const QRMini = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 8px;
`;

const LinksList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  overflow-y: auto;
  padding-right: 0.5rem;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(139, 92, 246, 0.5);
    border-radius: 2px;
  }
`;

const LinkItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  color: rgba(255, 255, 255, 0.6);

  &:hover {
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(139, 92, 246, 0.4);
    color: #fff;
  }
`;

const LinkIconWrapper = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
`;

const LinkIconImg = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: contain;
`;

const LinkText = styled.div`
  flex: 1;
  min-width: 0;
`;

const LinkTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #fff;
`;

const LinkSub = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Footer = styled.footer`
  position: absolute;
  bottom: 1rem;
  text-align: center;
`;

const FooterText = styled.p`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.3);
`;

const LoadingCard = styled.div`
  width: 380px;
  height: 520px;
  border-radius: 24px;
  background: rgba(20, 20, 35, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
`;

const PulseRing = styled.div`
  width: 80px;
  height: 80px;
  border: 3px solid transparent;
  border-top-color: #8b5cf6;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  letter-spacing: 2px;
  text-transform: uppercase;
`;

export default LinkTree3D;
